import { Injectable, Signal, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { WkTranslatePipe, NestedValuePipe, normalizeForSearch, shouldPreserveSearchPunctuation } from "@shared/utils";
import type { GroupResult } from "@progress/kendo-data-query";

export interface DropdownSearchConfig {
    // Option list to filter.
    data: Signal<any[]>;
    // Path to the label field on each item; resolved (and translated) into the text to match.
    textField: Signal<string>;
    // Gating for the search, enabled when true
    filterable?: Signal<boolean>;
}

// Debounced, diacritic-insensitive in-memory search shared by the dropdown wrappers.
@Injectable()
export class DropdownSearchService {
    private static readonly DEBOUNCE_MS = 150;
    private static readonly MIN_CHARS = 1; // filter from the first character, per the UI spec
    private static readonly MIN_OPTIONS_FOR_FILTER = 5; // the search bar appears only past this many options

    private readonly wkTranslate = inject(WkTranslatePipe);
    private readonly nestedValue = inject(NestedValuePipe);
    private readonly termInput$ = new Subject<string>();

    private config?: DropdownSearchConfig;

    readonly filterTerm = signal("");

    // Cached normalized searchable label per item, rebuilt only when the data or textField changes.
    private readonly searchableLabels = computed(() => {
        const data = this.config?.data();
        const textField = this.config?.textField();

        const labels = { normalized: new Map<unknown, string>(), literal: new Map<unknown, string>() };
        if (!data?.length || !textField) return labels;

        for (const item of this.flattenItems(data)) {
            const label = this.wkTranslate.transform(this.nestedValue.transform(item, textField)) ?? "";
            labels.normalized.set(item, normalizeForSearch(label, false, true));
            labels.literal.set(item, normalizeForSearch(label, true, true));
        }
        return labels;
    });

    // List bound to the inner Kendo component. Recomputes when data or term changes.
    readonly filteredData = computed<any[]>(() => {
        const data = this.config?.data();
        if (!data) return [];

        const term = this.filterTerm();
        if (!term) return data;

        const preservePunctuation = shouldPreserveSearchPunctuation(term);
        const searchTerm = normalizeForSearch(term, preservePunctuation, true);
        if (!searchTerm) return [];
        const cachedLabels = this.searchableLabels();
        const labels = preservePunctuation ? cachedLabels.literal : cachedLabels.normalized;

        if (this.isGrouped(data)) {
            return this.filterAndSortByMatch(this.flattenItems(data), labels, searchTerm);
        }

        return this.filterAndSortByMatch(data, labels, searchTerm);
    });

    // Search visibility: gated on `filterable` and the option-count threshold (flattened for groups).
    readonly isFilterable = computed(() => {
        const filterable = this.config?.filterable?.();
        const data = this.config?.data();

        if (!filterable) return false;

        const optionCount = data ? this.flattenItems(data).length : 0;
        return optionCount > DropdownSearchService.MIN_OPTIONS_FOR_FILTER;
    });

    constructor() {
        // Debounces and gates the filter term exposed to consumers
        this.termInput$
            .pipe(
                debounceTime(DropdownSearchService.DEBOUNCE_MS),
                map((t) => (t ?? "").trim()),
                map((t) => (t.length >= DropdownSearchService.MIN_CHARS ? t : "")),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe((term) => this.filterTerm.set(term));
    }

    public connect(config: DropdownSearchConfig): void {
        this.config = config;
    }

    public onFilterChange(term: string): void {
        this.termInput$.next(term);
    }

    public reset(): void {
        this.filterTerm.set("");
        this.termInput$.next("");
    }

    // Narrows to Kendo's `groupBy` output so grouped dropdowns filter their options, not the group wrappers.
    private isGrouped(data: any[]): data is GroupResult[] {
        return (
            data.length > 0 &&
            data.every((entry): entry is GroupResult => typeof entry === "object" && entry !== null && "field" in entry && Array.isArray(entry.items))
        );
    }

    private flattenItems(data: any[]): any[] {
        return this.isGrouped(data) ? data.flatMap((group) => group.items) : data;
    }

    private filterAndSortByMatch(items: any[], labels: Map<unknown, string>, term: string): any[] {
        const matches: { item: any; matchIndex: number }[] = [];

        for (const item of items) {
            const matchIndex = labels.get(item)?.indexOf(term) ?? -1;
            if (matchIndex >= 0) matches.push({ item, matchIndex });
        }

        matches.sort((a, b) => a.matchIndex - b.matchIndex);
        return matches.map(({ item }) => item);
    }
}
